import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCampaignMutations, useCampaignPrompts } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignPrompt, slugifyKey, uniqueKey } from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { arrToLines, byOrder, linesToArr, nextOrderOf, signature } from './builderShared';
import { TokenTextField } from './TokenTextField';

/**
 * Admin editor for a campaign's AI flow definitions (`CampaignPrompt`) — the data-driven replacement
 * for the guided runner's hard-coded prompt strings. `flow_key` is the stable machine id the runtime
 * resolves a flow by (renaming it detaches the flow from its bundled fallback), `template` is the
 * prompt body with `{token}` placeholders the runtime fills from live grounding, `model` optionally
 * overrides the flow's default model, and `grounding_inputs` is a declarative hint (one per line) of
 * which tokens the template expects. `output_schema` and `metadata` are preserved untouched across
 * saves (full-record PUT), so authoring structured output stays possible without a bespoke field here.
 */
export function PromptsEditor(props: { campaign: Campaign; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  const { data: rows = [], isLoading } = useCampaignPrompts(campaign.id);
  const mut = useCampaignMutations('prompt');

  const [prompts, setPrompts] = React.useState<CampaignPrompt[]>([]);
  const sig = signature(rows);
  React.useEffect(() => {
    setPrompts(byOrder(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const patchLocal = (id: number, next: Partial<CampaignPrompt>) =>
    setPrompts((prev) => prev.map((p) => (p.id === id ? { ...p, ...next } : p)));

  const save = (row: CampaignPrompt, next: Partial<CampaignPrompt>) => {
    patchLocal(row.id, next);
    mut.update.mutate({ ...row, ...next, id: row.id });
  };

  const add = () => {
    const flow_key = uniqueKey('flow', prompts.map((p) => p.flow_key));
    mut.create.mutate({
      campaign: campaign.id,
      flow_key,
      label: '',
      template: '',
      model: null,
      grounding_inputs: [],
      order: nextOrderOf(prompts),
      hidden: false,
      metadata: {},
    });
  };

  const remove = (row: CampaignPrompt) => {
    setPrompts((prev) => prev.filter((p) => p.id !== row.id));
    mut.remove.mutate(row.id);
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="AI prompts"
        subtitle="The campaign's AI flow templates. Use {token} placeholders (filled from the flow's live grounding) and {{group.key}} references (any campaign attribute — insert them with the picker below each template). The flow key is the stable id the runtime resolves by."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {prompts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No prompts yet. Without any, each AI flow falls back to its bundled template.
            </Typography>
          )}

          <Stack spacing={2}>
            {prompts.map((row) => (
              <Box
                key={row.id}
                sx={{
                  border: '1px solid var(--cb-border, #e5e7eb)',
                  borderRadius: 2,
                  p: 1.5,
                  opacity: row.hidden ? 0.55 : 1,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Flow key"
                    size="small"
                    value={row.flow_key}
                    disabled={!canWrite}
                    onChange={(e) => patchLocal(row.id, { flow_key: slugifyKey(e.target.value) })}
                    onBlur={() => save(row, { flow_key: row.flow_key })}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Label"
                    size="small"
                    fullWidth
                    value={row.label}
                    disabled={!canWrite}
                    onChange={(e) => patchLocal(row.id, { label: e.target.value })}
                    onBlur={() => save(row, { label: row.label })}
                  />
                  <TextField
                    label="Model (optional)"
                    size="small"
                    value={row.model ?? ''}
                    disabled={!canWrite}
                    onChange={(e) => patchLocal(row.id, { model: e.target.value })}
                    onBlur={() => save(row, { model: row.model?.trim() ? row.model : null })}
                    sx={{ minWidth: 200 }}
                  />
                  {canWrite && (
                    <Stack direction="row" alignItems="center">
                      <Tooltip title={row.hidden ? 'Hidden from the runner — show' : 'Hide from the runner'}>
                        <IconButton size="small" onClick={() => save(row, { hidden: !row.hidden })}>
                          {row.hidden ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove prompt">
                        <IconButton size="small" onClick={() => remove(row)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
                <TokenTextField
                  label="Template"
                  value={row.template}
                  disabled={!canWrite}
                  campaignId={campaign.id}
                  onChange={(next) => patchLocal(row.id, { template: next })}
                  onBlur={() => save(row, { template: row.template })}
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Grounding inputs (one token per line)"
                  size="small"
                  fullWidth
                  multiline
                  value={arrToLines(row.grounding_inputs)}
                  disabled={!canWrite}
                  onChange={(e) => patchLocal(row.id, { grounding_inputs: linesToArr(e.target.value) })}
                  onBlur={() => save(row, { grounding_inputs: row.grounding_inputs })}
                />
              </Box>
            ))}
          </Stack>

          {canWrite && (
            <Box sx={{ mt: 1.5 }}>
              <GhostButton onClick={add} disabled={mut.isSaving}>
                + Add prompt
              </GhostButton>
            </Box>
          )}
        </>
      )}
    </div>
  );
}

export default PromptsEditor;
